import { DefaultCatchBoundary } from "@acme/ui/default-catch-boundary";
import { NotFound } from "@acme/ui/not-found";
import * as Sentry from "@sentry/tanstackstart-react";
import { QueryCache, QueryClient } from "@tanstack/react-query";
import type { AnyRoute } from "@tanstack/react-router";
import { createRouter as createTanStackRouter } from "@tanstack/react-router";
import { setupRouterSsrQueryIntegration } from "@tanstack/react-router-ssr-query";
import superjson from "superjson";
import { previewScrubbingHooks } from "./sentry-scrub";

// Exported so the web root can derive its dns-prefetch hint for the ingest
// host from the same value instead of repeating it.
export const SENTRY_DSN =
  "https://25d305ae674731dfa741cb2aaff91a6f@o4510964536311808.ingest.de.sentry.io/4510964537753680";

interface CreateRouterOptions<TRouteTree extends AnyRoute> {
  rewrite?: {
    input?: (opts: { url: URL }) => URL;
    output?: (opts: { url: URL }) => URL;
  };
  routeTree: TRouteTree;
  sentryEnvironment: string;
}

export function createRouter<TRouteTree extends AnyRoute>({
  routeTree,
  rewrite,
  sentryEnvironment,
}: CreateRouterOptions<TRouteTree>) {
  const queryClient = new QueryClient({
    defaultOptions: {
      dehydrate: { serializeData: superjson.serialize },
      hydrate: { deserializeData: superjson.deserialize },
      queries: {
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60,
      },
    },
    queryCache: new QueryCache(),
  });

  const router = createTanStackRouter({
    context: { queryClient, user: null },
    defaultErrorComponent: DefaultCatchBoundary,
    defaultNotFoundComponent: () => <NotFound />,
    defaultPreload: "intent",
    defaultPreloadStaleTime: 0,
    defaultStructuralSharing: true,
    rewrite,
    routeTree,
    scrollRestoration: true,
  });

  setupRouterSsrQueryIntegration({
    handleRedirects: true,
    queryClient,
    router,
    wrapQueryClient: true,
  });

  if (!router.isServer) {
    Sentry.init({
      // A reviewer-preview link is a bearer credential: no event,
      // transaction, breadcrumb or span leaves the browser with its token in it.
      ...previewScrubbingHooks,
      dsn: SENTRY_DSN,
      environment: sentryEnvironment,
      // The router's own tracing only. It is a browserTracingIntegration too
      // (both are named "BrowserTracing"), so a plain one listed after it
      // replaced it, and spans were named by the raw URL, token and all,
      // instead of the route template.
      integrations: [Sentry.tanstackRouterBrowserTracingIntegration(router)],
      profileLifecycle: "trace",
      profileSessionSampleRate: 0.15,
      tracePropagationTargets: [
        "localhost",
        // biome-ignore lint/performance/useTopLevelRegex: valid use case
        /^https:\/\/lindevo\.com\//,
        // biome-ignore lint/performance/useTopLevelRegex: valid use case
        /^https:\/\/cms\.lindevo\.com\//,
      ],
      tracesSampleRate: 0.15,
    });
  }

  return router;
}
